import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';
import { classNames } from '@library/utils';

// Temporary workaround due to upstream issues with types;
// See https://github.com/shadcn-ui/ui/issues/120
type CN<T> = T & { className?: string };

const Chevron = ({ isActive = true }: { isActive?: boolean }) => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    className="size-4 shrink-0 transition-transform duration-200"
  >
    <path
      d="M17 9.5L12 15.5L7 9.5"
      stroke={isActive ? 'white' : '#6D6C77'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Bullet = ({ isActive = true }: { isActive?: boolean }) => (
  <svg
    fill={isActive ? 'white' : '#6D6C77'}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M 8.096 12.003 C 8.096 13.091 8.48 14.019 9.248 14.787 C 10.016 15.555 10.933 15.928 12 15.907 C 13.067 15.886 13.984 15.512 14.752 14.787 C 15.52 14.062 15.904 13.134 15.904 12.003 C 15.904 10.872 15.52 9.955 14.752 9.251 C 13.984 8.547 13.067 8.163 12 8.099 C 10.933 8.035 10.016 8.419 9.248 9.251 C 8.48 10.083 8.096 11 8.096 12.003 Z" />
  </svg>
);

const Accessory = ({
  chevronStyle,
  isActive = true
}: {
  chevronStyle: 'chevron' | 'bullet';
  isActive?: boolean;
}) =>
  chevronStyle === 'chevron' ? (
    <Chevron isActive={isActive} />
  ) : (
    <Bullet isActive={isActive} />
  );

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  CN<React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>> & {
    isActive?: boolean;
  }
>(({ className = '', isActive = true, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={classNames(
      'text-sm',
      isActive
        ? 'text-soClean-100 hover:decoration-soClean-100'
        : 'text-soFresh-160 hover:decoration-soFresh-160',
      className
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';
AccordionItem.defaultProps = { className: '', isActive: true };

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  CN<React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>> & {
    chevronPosition: string;
    chevronStyle?: 'chevron' | 'bullet';
    labelAsTrigger?: boolean;
    isActive?: boolean;
  }
>(
  (
    {
      className = '',
      chevronPosition = 'right',
      chevronStyle = 'chevron',
      labelAsTrigger = true,
      isActive = true,
      children,
      ...props
    },
    ref
  ) => (
    <AccordionPrimitive.Header className="flex">
      {labelAsTrigger ? (
        <AccordionPrimitive.Trigger
          ref={ref}
          className={classNames(
            'flex flex-1 items-center py-2 pl-2 font-medium transition-all hover:underline [&[data-state=closed]>svg]:-rotate-90',
            chevronPosition === 'right' ? 'justify-between' : 'gap-2',
            className
          )}
          {...props}
        >
          {chevronPosition === 'left' && (
            <Accessory chevronStyle={chevronStyle} isActive={isActive} />
          )}
          {children}
          {chevronPosition === 'right' && (
            <Accessory chevronStyle={chevronStyle} isActive={isActive} />
          )}
        </AccordionPrimitive.Trigger>
      ) : (
        <div
          className={classNames(
            'flex flex-1 items-center py-2 pl-2 font-medium hover:underline',
            chevronPosition === 'right' ? 'justify-between' : 'gap-2',
            className
          )}
        >
          <AccordionPrimitive.Trigger
            ref={ref}
            className={classNames(
              'transition-all [&[data-state=closed]>svg]:-rotate-90'
            )}
            {...props}
          >
            {chevronPosition === 'left' && (
              <Accessory chevronStyle={chevronStyle} isActive={isActive} />
            )}
            {chevronPosition === 'right' && (
              <Accessory chevronStyle={chevronStyle} isActive={isActive} />
            )}
          </AccordionPrimitive.Trigger>
          {children}
        </div>
      )}
    </AccordionPrimitive.Header>
  )
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
AccordionTrigger.defaultProps = {
  className: '',
  chevronStyle: 'chevron',
  labelAsTrigger: true,
  isActive: true
};

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  CN<React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>>
>(({ className = '', children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all"
    {...props}
  >
    <div className={classNames(className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;
AccordionContent.defaultProps = { className: '' };

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
